import React, { useEffect, useRef, useState } from 'react';
import { ROUTES, ROUTE_META } from './tourSteps';

// 操作引导 overlay: 示例模式 banner, spotlight (box-shadow cut-out + 4px ring) on the step's
// data-tour target(s), numbered markers, and the step card. Re-measured every animation frame
// (targets move with scrolling, resizing and late data). No target → the card sits in the middle.
// Phones (< 640px): the card is a full-width sheet at the bottom.
// Also exports the first-open invite (TourInvite) and the done screens (TourDone).

const C = {
  brand: '#111111', ink: '#111111', body: '#3A3835', sub: '#6E685F', mute: '#6E685F',
  line: '#E4DFD7', line2: '#D6D0C7', soft: '#F6F3EE', ring: '#B8975A', dim: 'rgba(32, 24, 22, .55)', bar: '#E4DFD7',
};
const Z = { block: 4000, hole: 4001, mark: 4002, card: 4003, banner: 4004 };
const BANNER_H = 34;
const PAD = 6;
const font = "'Jost', 'Noto Sans SC', 'PingFang SC', -apple-system, 'Segoe UI', sans-serif";

const btn = (primary) => ({
  height: 36, padding: primary ? '0 16px' : '0 14px', borderRadius: 2, font: 'inherit', fontSize: 13.5, cursor: 'pointer', whiteSpace: 'nowrap',
  border: primary ? 'none' : `1px solid ${C.line2}`, background: primary ? C.brand : '#FFFDF9', color: primary ? '#FFFDF9' : C.ink, fontWeight: primary ? 600 : 400,
});

function findVisible(t) {
  const list = document.querySelectorAll(`[data-tour="${t}"]`);
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const r = list[i].getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return list[i];
  }
  return null;
}

export function DemoBanner({ onExit }) {
  return (
    <>
    {/* the page content moves down by the banner's height (body gets .mm-tour-demo while a tour runs) */}
    <style>{`body.mm-tour-demo .main-content { padding-top: ${BANNER_H}px; }`}</style>
    <div role="status" style={{
      position: 'fixed', top: 0, left: 0, right: 0, height: BANNER_H, zIndex: Z.banner, background: '#F3EADB', color: '#7A4A15',
      borderBottom: '1px solid #D9C49A', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
      padding: '0 12px', boxSizing: 'border-box', fontFamily: font, whiteSpace: 'nowrap', overflow: 'hidden',
    }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        <b style={{ fontWeight: 600 }}>示例模式</b> · 页面上的会员和数据都是示例，点任何按钮都不会保存
      </span>
      <button type="button" onClick={onExit} style={{ border: 'none', background: 'none', color: '#7A4A15', fontWeight: 600, font: 'inherit', fontSize: 13, cursor: 'pointer', textDecoration: 'underline', padding: 0, flex: '0 0 auto' }}>
        退出引导
      </button>
    </div>
    </>
  );
}

export function StepBars({ n, total }) {
  return (
    <div aria-label={`进度 ${n} / ${total}`} style={{ display: 'flex', gap: 5 }}>
      {Array.from({ length: total }, (_, i) => (
        <span key={i} style={{ width: 18, height: 5, borderRadius: 3, background: i < n ? C.brand : C.bar }} />
      ))}
    </div>
  );
}

export default function TourOverlay({ route, idx, step, total, stepKey, onNext, onPrev, onExit, onSkip }) {
  const [geo, setGeo] = useState({ rect: null, marks: [], w: window.innerWidth, h: window.innerHeight, cardH: 0 });
  const cardRef = useRef(null);
  const scrolledRef = useRef('');

  useEffect(() => {
    const targets = Array.isArray(step.target) ? step.target : step.target ? [step.target] : [];
    const markers = step.markers || [];
    let raf = 0;
    let last = '';
    const tick = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      let u = null;
      targets.forEach((t) => {
        const el = findVisible(t);
        if (!el) return;
        if (scrolledRef.current !== stepKey) {
          scrolledRef.current = stepKey;
          const r0 = el.getBoundingClientRect();
          if (r0.top < BANNER_H + 8 || r0.bottom > h - 8) {
            try { el.scrollIntoView({ block: 'center', inline: 'nearest' }); } catch (e) { el.scrollIntoView(); }
          }
        }
        const r = el.getBoundingClientRect();
        u = u ? { l: Math.min(u.l, r.left), t: Math.min(u.t, r.top), r: Math.max(u.r, r.right), b: Math.max(u.b, r.bottom) }
          : { l: r.left, t: r.top, r: r.right, b: r.bottom };
      });
      let rect = null;
      if (u) {
        const l = Math.max(2, u.l - PAD); const t = Math.max(BANNER_H + 2, u.t - PAD);
        const r = Math.min(w - 2, u.r + PAD); const b = Math.min(h - 2, u.b + PAD);
        if (r - l > 4 && b - t > 4) rect = { left: Math.round(l), top: Math.round(t), width: Math.round(r - l), height: Math.round(b - t) };
      }
      const marks = [];
      markers.forEach((m) => {
        const el = findVisible(m.t);
        if (!el) return;
        const r = el.getBoundingClientRect();
        marks.push({ n: m.n, hollow: !!m.hollow, x: Math.round(r.right - 8), y: Math.round(r.top - 12) });
      });
      const cardH = cardRef.current ? cardRef.current.offsetHeight : 0;
      const key = JSON.stringify([rect, marks, w, h, cardH]);
      if (key !== last) { last = key; setGeo({ rect, marks, w, h, cardH }); }
      raf = window.requestAnimationFrame(tick);
    };
    tick();
    return () => window.cancelAnimationFrame(raf);
  }, [stepKey, step]);

  const { rect, marks, w: vw, h: vh, cardH } = geo;
  const mobile = vw < 640;
  const n = idx + 1;
  const last = n === total;
  const list = Array.isArray(step.body);
  const W = Math.min(list ? 520 : 390, vw - 32);
  const meta = ROUTE_META[route] || ROUTE_META.coach;

  // ── card placement ──
  let cardStyle;
  let arrow = null;
  if (mobile) {
    cardStyle = { left: 0, right: 0, bottom: 0, width: 'auto', borderRadius: '2px 2px 0 0', maxHeight: '62vh', overflowY: 'auto' };
  } else if (!rect) {
    cardStyle = { left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: W };
  } else {
    const ch = cardH || 260;
    const gap = 14;
    const clampX = (x) => Math.max(16, Math.min(vw - W - 16, x));
    const clampY = (y) => Math.max(BANNER_H + 10, Math.min(vh - ch - 12, y));
    if (rect.top + rect.height + gap + ch <= vh - 12) {
      const left = clampX(rect.left);
      cardStyle = { left, top: rect.top + rect.height + gap, width: W };
      arrow = { side: 'top', at: Math.max(18, Math.min(W - 34, rect.left + Math.min(rect.width / 2, 70) - left - 8)) };
    } else if (rect.top - gap - ch >= BANNER_H + 10) {
      const left = clampX(rect.left);
      cardStyle = { left, top: rect.top - gap - ch, width: W };
      arrow = { side: 'bottom', at: Math.max(18, Math.min(W - 34, rect.left + Math.min(rect.width / 2, 70) - left - 8)) };
    } else if (rect.left + rect.width + gap + W <= vw - 12) {
      const top = clampY(rect.top);
      cardStyle = { left: rect.left + rect.width + gap, top, width: W };
      arrow = { side: 'left', at: Math.max(18, Math.min(ch - 34, rect.top + 20 - top)) };
    } else if (rect.left - gap - W >= 12) {
      const top = clampY(rect.top);
      cardStyle = { left: rect.left - gap - W, top, width: W };
      arrow = { side: 'right', at: Math.max(18, Math.min(ch - 34, rect.top + 20 - top)) };
    } else {
      cardStyle = { right: 16, bottom: 16, width: W };
    }
  }
  const arrowStyle = arrow ? {
    position: 'absolute', width: 16, height: 16, background: '#FFFDF9', transform: 'rotate(45deg)',
    ...(arrow.side === 'top' ? { top: -8, left: arrow.at } : arrow.side === 'bottom' ? { bottom: -8, left: arrow.at }
      : arrow.side === 'left' ? { left: -8, top: arrow.at } : { right: -8, top: arrow.at }),
  } : null;

  const blockers = rect ? [
    { left: 0, top: 0, width: '100%', height: rect.top },
    { left: 0, top: rect.top + rect.height, width: '100%', bottom: 0 },
    { left: 0, top: rect.top, width: rect.left, height: rect.height },
    { left: rect.left + rect.width, top: rect.top, right: 0, height: rect.height },
  ] : [{ left: 0, top: 0, right: 0, bottom: 0, background: C.dim }];

  return (
    <>
      <DemoBanner onExit={onExit} />
      {blockers.map((b, i) => <div key={i} aria-hidden="true" style={{ position: 'fixed', zIndex: Z.block, ...b }} />)}
      {rect && (
        <div aria-hidden="true" style={{
          position: 'fixed', zIndex: Z.hole, pointerEvents: 'none', borderRadius: 2, left: rect.left, top: rect.top, width: rect.width, height: rect.height,
          boxShadow: `0 0 0 4px ${C.ring}, 0 0 0 9999px ${C.dim}`, transition: 'left .18s, top .18s, width .18s, height .18s',
        }} />
      )}
      {marks.map((m) => (
        <span key={m.n} aria-hidden="true" style={{
          position: 'fixed', zIndex: Z.mark, left: m.x, top: m.y, width: 20, height: 20, borderRadius: '50%', boxSizing: 'border-box',
          background: m.hollow ? '#FFFDF9' : C.brand, color: m.hollow ? C.brand : '#FFFDF9', border: m.hollow ? `2px solid ${C.brand}` : 'none',
          fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: font, pointerEvents: 'none',
        }}>{m.n}</span>
      ))}
      <section ref={cardRef} role="dialog" aria-label="引导说明" style={{
        position: 'fixed', zIndex: Z.card, background: '#FFFDF9', borderRadius: 2, boxShadow: 'none',
        padding: mobile ? '16px 16px calc(16px + env(safe-area-inset-bottom))' : '18px 20px', boxSizing: 'border-box',
        display: 'flex', flexDirection: 'column', gap: 10, fontFamily: font, color: C.ink, ...cardStyle,
      }}>
        {arrowStyle && <div aria-hidden="true" style={arrowStyle} />}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: C.brand, letterSpacing: '.08em' }}>{meta.name} · 第 {n} / {total} 步</span>
          <button type="button" aria-label="关闭引导" onClick={onExit} style={{ border: 'none', background: 'none', color: C.mute, fontSize: 18, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>×</button>
        </div>
        <h2 style={{ margin: 0, fontFamily: "'Cormorant Garamond', 'Noto Serif SC', Georgia, serif", fontSize: 22, fontWeight: 500, lineHeight: 1.4, position: 'relative' }}>{step.title}</h2>
        {list ? (
          <ol style={{ margin: 0, paddingLeft: 20, fontSize: 14, lineHeight: 1.8, color: C.body, position: 'relative' }}>
            {step.body.map((x, i) => <li key={i}>{x}</li>)}
          </ol>
        ) : (
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.7, color: C.body, position: 'relative' }}>{step.body}</p>
        )}
        {step.rule && (
          <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, color: C.sub, background: C.soft, borderRadius: 2, padding: '8px 10px', position: 'relative' }}>{step.rule}</p>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginTop: 4, flexWrap: 'wrap', position: 'relative' }}>
          <StepBars n={n} total={total} />
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            {idx === 0
              ? <button type="button" onClick={onSkip} style={btn(false)}>跳过引导</button>
              : <button type="button" onClick={onPrev} style={btn(false)}>上一步</button>}
            <button type="button" onClick={onNext} style={btn(true)} autoFocus>{step.next || (last ? '完成' : '下一步')}</button>
          </div>
        </div>
      </section>
    </>
  );
}

// ── shared dialog shell ──
function Dialog({ label, width = 560, children, onBackdrop }) {
  return (
    <div onClick={onBackdrop} style={{ position: 'fixed', inset: 0, zIndex: Z.card, background: 'rgba(32, 24, 22, .5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: font }}>
      <section role="dialog" aria-modal="true" aria-label={label} onClick={(e) => e.stopPropagation()} style={{
        width: `min(${width}px, 100%)`, maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', background: '#FFFDF9', borderRadius: 2, boxShadow: 'none',
        padding: '28px 30px 24px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 16, color: C.ink,
      }}>
        {children}
      </section>
    </div>
  );
}

/** 第一次打开客户工作台 → 邀请看引导 (prototype Main). */
export function TourInvite({ defaultRoute = 'coach', progress, onStart, onLater }) {
  const [pick, setPick] = useState(defaultRoute);
  const card = (k) => {
    const on = pick === k;
    const meta = ROUTE_META[k];
    const p = progress && progress[k];
    return (
      <button key={k} type="button" aria-pressed={on} onClick={() => setPick(k)} style={{
        textAlign: 'left', border: on ? `2px solid ${C.brand}` : `1px solid ${C.line2}`, background: on ? '#EFEAE2' : '#FFFDF9', borderRadius: 2,
        padding: on ? '13px 15px' : '14px 16px', font: 'inherit', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0,
      }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: on ? C.brand : C.ink }}>{meta.name} · {ROUTES[k].length} 步</span>
        <span style={{ fontSize: 12.5, lineHeight: 1.6, color: C.body }}>{meta.blurb}</span>
        {p && !p.completed && Number(p.last_step) > 1 && <span style={{ fontSize: 12, color: '#7A4A15', fontWeight: 600 }}>看到第 {p.last_step} 步</span>}
      </button>
    );
  };
  return (
    <Dialog label="操作引导">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 12, letterSpacing: '.14em', color: C.brand, fontWeight: 700 }}>第一次使用客户工作台</span>
        <h1 style={{ margin: 0, fontFamily: "'Cormorant Garamond', 'Noto Serif SC', Georgia, serif", fontSize: 30, fontWeight: 500, lineHeight: 1.35 }}>花 3 分钟，跟着走一遍怎么用？</h1>
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.7, color: C.body }}>
          我们会在真实页面上一步一步高亮要点的地方。用的是一位<b style={{ fontWeight: 600 }}>示例会员</b>，你点的任何按钮都不会写进真实数据。
        </p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        {card('coach')}{card('supervisor')}
      </div>
      <div style={{ fontSize: 12.5, color: C.sub }}>系统按你的角色先选好了路线，也可以换一条。</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, borderTop: '1px solid #EFEAE2', paddingTop: 16, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, color: C.mute }}>以后可以在每个页签的「这是什么？」里重新打开</span>
        <div style={{ display: 'flex', gap: 10, marginLeft: 'auto' }}>
          <button type="button" onClick={onLater} style={{ ...btn(false), height: 40, padding: '0 16px', borderRadius: 2, color: C.body }}>以后再说</button>
          <button type="button" onClick={() => onStart(pick)} style={{ ...btn(true), height: 40, padding: '0 22px', borderRadius: 2 }}>开始（3 分钟）</button>
        </div>
      </div>
    </Dialog>
  );
}

/** Done screens (prototype CDone / SDone). onClose({ go?, weekly? }). */
export function TourDone({ route, weekly, onAgain, onClose }) {
  const [remind, setRemind] = useState(weekly !== false);
  if (route === 'supervisor') {
    return (
      <Dialog label="引导完成" onBackdrop={() => onClose({ weekly: remind })}>
        <h1 style={{ margin: 0, fontFamily: "'Cormorant Garamond', 'Noto Serif SC', Georgia, serif", fontSize: 28, fontWeight: 500, textAlign: 'center' }}>每周一次的监督清单</h1>
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.7, color: C.body, textAlign: 'center' }}>建议每周固定一个时间，在「服务监督」里按这个顺序走一遍：</p>
        <ol style={{ margin: 0, padding: '14px 18px 14px 36px', background: C.soft, borderRadius: 2, fontSize: 14, lineHeight: 1.9 }}>
          <li>哪些会员超过 48 小时没联系、7 天没进展、14 天没推荐（红黄灯）</li>
          <li>哪位老师的 KPI 是红色</li>
          <li>待审核的联系记录、频率申请、月报都处理完</li>
          <li>不达标的开整改单，上周的整改单逐一核实</li>
        </ol>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13.5, color: C.body, cursor: 'pointer' }}>
          <input type="checkbox" checked={remind} onChange={(e) => setRemind(e.target.checked)} style={{ accentColor: C.brand }} />
          每周一上午 10:00 提醒我做一次
        </label>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 4 }}>
          <button type="button" onClick={() => { onClose({ weekly: remind }); onAgain(); }} style={{ ...btn(false), height: 40, padding: '0 16px', borderRadius: 2 }}>再看一遍</button>
          <button type="button" onClick={() => onClose({ weekly: remind, go: true })} style={{ ...btn(true), height: 40, padding: '0 22px', borderRadius: 2 }}>去服务监督</button>
        </div>
      </Dialog>
    );
  }
  return (
    <Dialog label="引导完成" width={520} onBackdrop={() => onClose({})}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center', textAlign: 'center' }}>
        <span style={{ width: 56, height: 56, borderRadius: '50%', background: '#E7EFE9', color: '#3F7D5A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><path d="M5 12l5 5 9-10" /></svg>
        </span>
        <h1 style={{ margin: 0, fontFamily: "'Cormorant Garamond', 'Noto Serif SC', Georgia, serif", fontSize: 28, fontWeight: 500 }}>你已经会用客户工作台了</h1>
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.8, color: C.body }}>
          每天的顺序：<b style={{ fontWeight: 600 }}>看今日跟进 → 打开会员 → 按「下一步」做 → 写联系记录</b>。答应会员的事记进备忘录，做完一步在服务进度里打勾，每月写报告。
        </p>
        <div style={{ width: '100%', background: C.soft, borderRadius: 2, padding: '12px 14px', fontSize: 13, lineHeight: 1.7, color: C.body, textAlign: 'left', boxSizing: 'border-box' }}>
          <b style={{ fontWeight: 600, color: C.ink }}>十条铁律里最常用的三条</b><br />
          不知道的信息，不猜 · 答应会员的事情，必须进 CRM · 会员不主动，老师必须主动
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <button type="button" onClick={() => { onClose({}); onAgain(); }} style={{ ...btn(false), height: 40, padding: '0 16px', borderRadius: 2 }}>再看一遍</button>
          <button type="button" onClick={() => onClose({})} style={{ ...btn(true), height: 40, padding: '0 22px', borderRadius: 2 }}>开始工作</button>
        </div>
        <span style={{ fontSize: 12, color: C.mute }}>示例模式已退出，接下来你看到的都是真实会员。</span>
      </div>
    </Dialog>
  );
}
